<template>
  <gg-modal :is-active="opened" @close="close">
    <template v-slot:toolbar>
      <template v-if="mode === 'database'">
        Add Database User
      </template>
      <template v-else>
        Add User Database
      </template>
    </template>
    <template v-slot:body>
      <v-form>
        <v-row>
          <v-col cols="12">
            <p class="mb-0">
              <template v-if="mode === 'database'">
                Adding a User to the database “{{ database }}”.
              </template>
              <template v-else>
                Adding a database privileges for the user “{{ user }}”.
              </template>
            </p>
          </v-col>
          <v-col cols="12" class="pb-0">
            <template v-if="mode === 'database'">
              <template v-if="mysqlUsers.length">
                <gg-select
                  class="select-user"
                  label="Select Database User"
                  placeholder="Select a User"
                  :loading="loadingUsers"
                  :disabled="loadingPrivileges"
                  :items="mysqlUsers"
                  item-text="user"
                  v-model="selectedUser"
                  v-validate="'required'"
                  data-vv-name="user"
                  data-vv-as="User"
                  :error-messages="errors.collect('user')"
                />
                <router-link :to="{ name: 'hosting_mysql_users' }">
                  Create a new database user.
                </router-link>
              </template>
              <!-- If No Users are Available -->
              <p v-else>
                You currently have no active database users.
                <router-link :to="{ name: 'hosting_mysql_users', query: $route.query }">
                  Create a new database user.
                </router-link>
              </p>
            </template>
            <gg-select
              v-else
              label="Database"
              placeholder="Select a Database"
              dense outlined
              :loading="loadingDatabases"
              :disabled="loadingPrivileges"
              :items="mysqlDatabases"
              item-text="db"
              v-model="selectedDatabase"
              v-validate="'required'"
              data-vv-name="database"
              data-vv-as="Database"
              :error-messages="errors.collect('database')"
            ></gg-select>
          </v-col>
          <v-col cols="12" class="text-center" v-if="loadingPrivileges">
            <v-progress-circular size="64" indeterminate color="primary" />
          </v-col>
          <v-expand-transition>
            <v-col v-if="!loadingPrivileges && (selectedDatabase || selectedUser)" cols="12" class="mb-4">
              <v-row>
                <v-col cols="12" class="py-0">
                  <!-- Hidden input for validation purposes -->
                  <v-input
                    class="d-none"
                    v-validate="'required'"
                    :value="userPrivileges.join('')"
                    name="User Privileges"
                    data-vv-name="privileges"
                    data-vv-as="User Privileges"
                  />
                  <v-switch
                    color="primary"
                    v-model="allPrivilegesInit"
                    label="All Privileges"
                    @click="toggleAll"
                    :error-messages="errors.collect('privileges')"
                  />
                </v-col>
                <v-col
                  v-for="privilege in mysqlPrivileges"
                  :key="privilege"
                  class="py-0"
                  cols="12"
                  md="6"
                >
                  <v-checkbox
                    v-model="userPrivileges"
                    :label="privilege"
                    :value="privilege"
                    color="primary"
                    hide-details
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-expand-transition>
        </v-row>
      </v-form>
    </template>
    <template v-slot:actions>
      <v-spacer/>
      <gg-btn secondary @click="close">Cancel</gg-btn>
      <gg-btn
        v-if="mysqlUsers.length"
        :loading="loading"
        @click="submit"
      >
        Save Privileges
      </gg-btn>
    </template>
  </gg-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

const ALL_PRIVILEGES = 'ALL PRIVILEGES'

export default {
  name: 'AddUserModal',
  props: {
    opened: {
      type: Boolean,
      required: true
    },
    database: {
      type: String,
      required: false
    },
    user: {
      type: String,
      required: false
    },
    mode: {
      type: String,
      default: 'database',
      validator (val) {
        return ['user', 'database'].includes(val)
      }
    }
  },
  data () {
    return {
      loadingUsers: false,
      loadingDatabases: false,
      loadingPrivileges: false,
      loading: false,
      selectedUser: null,
      selectedDatabase: null,
      allPrivileges: false,
      allPrivilegesInit: false,
      userPrivileges: [],
      beginningPrivileges: []
    }
  },
  computed: mapGetters({
    mysqlPrivileges: 'mysql/mysqlPrivileges',
    mysqlDatabases: 'mysql/mysqlDatabases',
    mysqlUsers: 'mysql/mysqlUsers'
  }),
  methods: {
    ...mapActions({
      getMysqlUserPrivileges: 'mysql/getMysqlUserPrivileges',
      setMysqlUserPrivileges: 'mysql/setMysqlUserPrivileges',
      revokeMysqlUserAccess: 'mysql/revokeMysqlUserAccess',
      getMysqlPrivileges: 'mysql/getMysqlPrivileges',
      getMysqlDatabases: 'mysql/getMysqlDatabases',
      getMysqlUsers: 'mysql/getMysqlUsers',
      reportSuccess: 'reportSuccess',
      reportError: 'reportError'
    }),
    toggleAll () {
      this.allPrivileges = !this.allPrivileges
      if (this.allPrivileges) {
        this.userPrivileges = [...this.mysqlPrivileges]
      } else {
        this.userPrivileges = []
      }
    },
    async submit () {
      const valid = await this.$validator.validateAll()
      if (!valid) return
      this.loading = true
      const privileges = this.userPrivileges.join(',')
      const user = this.mode === 'database' ? this.selectedUser : this.user
      const database = this.mode === 'user' ? this.selectedDatabase : this.database
      try {
        await this.setMysqlUserPrivileges({
          user,
          database,
          privileges,
          _username: this.$route.query.user
        })
        this.loading = false
        this.reportSuccess(`Added privileges to DB “${database}” for user “${user}”.`)
        this.$emit('action-completed', 'privileges-added')
        this.close()
      } catch (e) {
        this.reportError(e)
        this.loading = false
      }
    },
    close () {
      this.$emit('close')
    },
    async loadPrivileges (data) {
      this.loadingPrivileges = true
      // load privileges
      try {
        const privileges = await this.getMysqlUserPrivileges({ ...data, _username: this.$route.query.user })
        this.allPrivileges = privileges.indexOf(ALL_PRIVILEGES) > -1
        this.allPrivilegesInit = this.allPrivileges
        if (!this.allPrivileges) {
          this.userPrivileges = [...privileges]
        } else {
          this.userPrivileges = [...this.mysqlPrivileges]
        }
        this.beginningPrivileges = [...privileges]
      } catch (e) {
        this.reportError(e)
      }
      this.loadingPrivileges = false
    }
  },
  watch: {
    selectedUser (val) {
      if (!val) return
      const data = {
        user: val,
        database: this.database
      }
      this.loadPrivileges(data)
    },
    selectedDatabase (val) {
      if (!val) return
      const data = {
        user: this.user,
        database: val
      }
      this.loadPrivileges(data)
    },
  },
  async mounted () {
    if (this.mode === 'database') {
      this.loadingUsers = true
      try {
        await this.getMysqlUsers(this.$route.query.user)
        await this.getMysqlPrivileges(this.$route.query.user)
      } catch (e) {
        this.reportError(e)
      }
      this.loadingUsers = false
    } else {
      this.loadingDatabases = true
      try {
        await this.getMysqlDatabases(this.$route.query.user)
        await this.getMysqlPrivileges(this.$route.query.user)
      } catch (e) {
        this.reportError(e)
      }
      this.loadingDatabases = false
    }
  },
}
</script>

<style>
.select-user .v-text-field__details {
  margin-bottom: 0 !important;
}
</style>
